import { useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { useBulkActions } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { Expensable } from 'modules/expenses/types/Expensable'

interface ToggleExpensablePayrollStatusButtonProps {
  expensable: Expensable
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  onLoadingChange?: (loading: boolean) => void
  disabled?: boolean
}

const ToggleExpensablePayrollStatusButton = ({
  expensable,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  onLoadingChange,
  disabled,
}: ToggleExpensablePayrollStatusButtonProps) => {
  const { section } = useOutletContext<RouteContextParams>()

  const { addToPayroll, removeFromPayroll, addToPayment, submitting } = useBulkActions()

  useEffect(() => {
    onLoadingChange?.(submitting)
  }, [submitting, onLoadingChange])

  const isApproved = expensable.status == 'approved'

  const isManual = expensable?.expense?.creationType == 'manual' || expensable.type !== 'expense'
  const isReimbursableExpensable =
    expensable.type != 'expense' || expensable.expense?.payment === 'reimbursable'

  const canBeReimbursed = isApproved && isManual && isReimbursableExpensable

  const showAddExpensableToPayrollButton =
    canBeReimbursed && reimburseThroughPayrollEnabled && !isMyExpensesSection(section)

  const hasPayrollResource = expensable.supplement?.id || expensable.compensation?.id

  const showRemoveFromPayrollButton =
    expensable.status === 'in_payroll' && hasPayrollResource && !isMyExpensesSection(section)

  const isReimbursableThroughSepaExpense =
    expensable.type == 'expense' &&
    expensable.expense?.payment === 'reimbursable' &&
    (expensable?.reimbursableCurrency || expensable?.currency)

  const showReimburseThroughSepaButton =
    isApproved &&
    isManual &&
    isReimbursableThroughSepaExpense &&
    reimburseThroughSepaEnabled &&
    !isMyExpensesSection(section)

  const showCombinedReimburseButton =
    showAddExpensableToPayrollButton && showReimburseThroughSepaButton

  if (showCombinedReimburseButton) {
    return (
      <Button.Split
        type="primary"
        label={i18n.t('expenses_v2.buttons.reimburse_through_payroll')}
        disabled={submitting || disabled}
        onClick={addToPayroll}
      >
        <Button.Split.Option onClick={addToPayment} disabled={submitting}>
          {i18n.t('expenses_v2.buttons.reimburse_via_sepa')}
        </Button.Split.Option>
      </Button.Split>
    )
  }

  if (showAddExpensableToPayrollButton) {
    return (
      <Button type="primary" onClick={addToPayroll} disabled={submitting || disabled}>
        {i18n.t('expenses_v2.buttons.reimburse_through_payroll')}
      </Button>
    )
  }

  if (showRemoveFromPayrollButton) {
    return (
      <Button
        type="primary"
        onClick={removeFromPayroll}
        disabled={submitting || disabled}
        loading={submitting}
        iconName={submitting ? 'upload' : undefined}
      >
        {i18n.t('expenses_v2.buttons.remove_from_payroll')}
      </Button>
    )
  }
}
export default ToggleExpensablePayrollStatusButton
